/**
 * UniversalScanner - Universal QR/Barcode Scanner for all modules
 * Works across: Check-in, Meals, Housing, Gate/Security, Stock
 * Supports both camera scanning and manual input
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Html5Qrcode, Html5QrcodeScanType } from 'html5-qrcode';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';
import {
  Camera, X, Check, AlertCircle, RefreshCw, Loader2,
  QrCode, ScanLine, Keyboard, CameraOff
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Scan modes configuration
const SCAN_CONFIGS = {
  checkin: {
    label: 'Check-in',
    description: 'Scan delegate badge for event check-in',
    endpoint: (eventId) => `/api/events/${eventId}/checkin`,
    bodyKey: 'qr_data',
    successMessage: 'Check-in successful',
    color: 'emerald'
  },
  meal: {
    label: 'Meal Scan',
    description: 'Verify meal entitlement and dining access',
    endpoint: (eventId) => `/api/events/${eventId}/meal-scan`,
    bodyKey: 'qr_data',
    extraFields: ['meal_type', 'dining_zone'],
    successMessage: 'Meal access verified',
    color: 'orange'
  },
  accommodation: {
    label: 'Accommodation Scan',
    description: 'Verify accommodation access',
    endpoint: (eventId) => `/api/events/${eventId}/accommodation-scan`,
    bodyKey: 'qr_data',
    extraFields: ['building', 'access_point'],
    successMessage: 'Accommodation access verified',
    color: 'indigo'
  },
  security: {
    label: 'Security Scan',
    description: 'Verify zone/area access',
    endpoint: (eventId) => `/api/events/${eventId}/security-scan`,
    bodyKey: 'qr_data',
    extraFields: ['access_zone', 'gate'],
    successMessage: 'Security access verified',
    color: 'red'
  },
  gate_entry: {
    label: 'Gate Entry',
    description: 'Log campus entry',
    endpoint: (eventId) => `/api/admin/events/${eventId}/campus-entry/delegate/scan`,
    bodyKey: 'qr_data',
    successMessage: 'Entry logged',
    color: 'blue'
  },
  gate_exit: {
    label: 'Gate Exit',
    description: 'Log campus exit',
    endpoint: (eventId) => `/api/admin/events/${eventId}/campus-exit/scan`,
    bodyKey: 'qr_data',
    successMessage: 'Exit logged',
    color: 'purple'
  },
  housing_checkin: {
    label: 'Housing Check-in',
    description: 'Room check-in',
    endpoint: (eventId) => `/api/events/${eventId}/accommodation-scan`,
    bodyKey: 'qr_data',
    extraFields: ['building'],
    successMessage: 'Housing check-in complete',
    color: 'indigo'
  },
  housing_checkout: {
    label: 'Housing Check-out',
    description: 'Room check-out',
    endpoint: (eventId) => `/api/admin/events/${eventId}/housing/check-out`,
    bodyKey: 'qr_data',
    successMessage: 'Housing check-out complete',
    color: 'pink'
  },
  stock: {
    label: 'Stock Item',
    description: 'Scan item barcode',
    endpoint: (eventId) => `/api/admin/events/${eventId}/stock/scan`,
    bodyKey: 'barcode',
    successMessage: 'Item scanned',
    color: 'cyan'
  }
};

const UniversalScanner = ({ 
  eventId, 
  mode = 'checkin',
  onScanSuccess,
  onScanError,
  showStats = true,
  compact = false,
  autoStart = false
}) => {
  const [isScanning, setIsScanning] = useState(false);
  const [showCameraDialog, setShowCameraDialog] = useState(false);
  const [manualInput, setManualInput] = useState('');
  const [lastResult, setLastResult] = useState(null);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [cameras, setCameras] = useState([]);
  const [activeCamera, setActiveCamera] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  
  const html5QrRef = useRef(null);
  const scannerContainerId = `scanner-${mode}-${eventId}`;
  
  const config = SCAN_CONFIGS[mode] || SCAN_CONFIGS.checkin;
  
  // Get available cameras
  useEffect(() => {
    Html5Qrcode.getCameras()
      .then(devices => {
        setCameras(devices);
        // Prefer back camera on mobile
        const backCamera = devices.find(d => 
          d.label.toLowerCase().includes('back') || 
          d.label.toLowerCase().includes('rear') ||
          d.label.toLowerCase().includes('environment')
        );
        setActiveCamera(backCamera?.id || devices[0]?.id);
      })
      .catch(err => {
        console.error('Error getting cameras:', err);
        setCameraError('Unable to access camera. Please check permissions.');
      });
    
    return () => {
      stopScanning();
    };
  }, []);

  const startScanning = async () => {
    if (!activeCamera) {
      toast.error('No camera available');
      return;
    }

    setCameraError(null);
    setError(null);
    setLastResult(null);
    setShowCameraDialog(true);

    // Small delay to ensure dialog is rendered
    setTimeout(async () => {
      try {
        html5QrRef.current = new Html5Qrcode(scannerContainerId);
        
        await html5QrRef.current.start(
          activeCamera,
          {
            fps: 15,
            qrbox: { width: 250, height: 250 },
            aspectRatio: 1.0,
          },
          handleScanResult,
          () => {} // Ignore scan failures
        );
        
        setIsScanning(true);
      } catch (err) {
        console.error('Failed to start scanner:', err);
        setCameraError('Failed to start camera. Please try again or use manual input.');
        setShowCameraDialog(false);
      }
    }, 300);
  };

  const stopScanning = async () => {
    if (html5QrRef.current) {
      try {
        if (html5QrRef.current.isScanning) {
          await html5QrRef.current.stop();
        }
        html5QrRef.current.clear();
        html5QrRef.current = null;
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
    }
    setIsScanning(false);
  };

  const handleCloseDialog = async () => {
    await stopScanning();
    setShowCameraDialog(false);
  };

  const handleScanResult = useCallback(async (decodedText) => {
    if (isProcessing) return;
    
    setIsProcessing(true);
    
    // Pause scanning
    if (html5QrRef.current) {
      try {
        await html5QrRef.current.pause();
      } catch (e) {}
    }

    await processCode(decodedText);
  }, [isProcessing, eventId, mode]);

  const processCode = async (code) => {
    if (!code.trim()) {
      setIsProcessing(false);
      return;
    }

    try {
      const token = getToken();
      const endpoint = config.endpoint(eventId);
      
      const body = {};
      body[config.bodyKey] = code;
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      // Check both HTTP status and response body success flag
      const isSuccess = response.ok && data.success !== false;

      if (isSuccess) {
        setLastResult({
          success: true,
          data: data,
          message: data.message || config.successMessage
        });
        
        toast.success(data.message || config.successMessage, {
          description: data.delegate?.name || data.item?.name || ''
        });
        
        if (onScanSuccess) onScanSuccess(data);
        
        // Auto-close dialog after success
        setTimeout(() => {
          handleCloseDialog();
        }, 1500);
      } else {
        const errorMsg = data.message || data.detail || data.error || 'Scan failed';
        setLastResult({
          success: false,
          error: errorMsg,
          data: data
        });
        
        toast.error(errorMsg);
        
        if (onScanError) onScanError(data);
        
        // Resume scanning after error
        if (html5QrRef.current) {
          try {
            await html5QrRef.current.resume();
          } catch (e) {}
        }
      }
    } catch (err) {
      console.error('Scan error:', err);
      setError('Network error. Please try again.');
      toast.error('Network error. Please try again.');
      
      // Resume scanning after error
      if (html5QrRef.current) {
        try {
          await html5QrRef.current.resume();
        } catch (e) {}
      }
    } finally {
      setIsProcessing(false);
      setManualInput('');
    }
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualInput.trim()) {
      processCode(manualInput.trim());
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && manualInput.trim()) {
      processCode(manualInput.trim());
    }
  };

  const colorClasses = {
    emerald: 'border-emerald-300 bg-emerald-50 text-emerald-700',
    orange: 'border-orange-300 bg-orange-50 text-orange-700',
    blue: 'border-blue-300 bg-blue-50 text-blue-700',
    purple: 'border-purple-300 bg-purple-50 text-purple-700',
    indigo: 'border-indigo-300 bg-indigo-50 text-indigo-700',
    pink: 'border-pink-300 bg-pink-50 text-pink-700',
    cyan: 'border-cyan-300 bg-cyan-50 text-cyan-700',
  };
  
  const buttonClasses = {
    emerald: 'bg-emerald-600 hover:bg-emerald-700',
    orange: 'bg-orange-600 hover:bg-orange-700',
    blue: 'bg-blue-600 hover:bg-blue-700',
    purple: 'bg-purple-600 hover:bg-purple-700',
    indigo: 'bg-indigo-600 hover:bg-indigo-700',
    pink: 'bg-pink-600 hover:bg-pink-700',
    cyan: 'bg-cyan-600 hover:bg-cyan-700',
  };

  if (compact) {
    return (
      <>
        <div className="flex gap-2">
          <Input
            value={manualInput}
            onChange={(e) => setManualInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter code or scan..."
            className="flex-1"
          />
          <Button
            onClick={startScanning}
            variant="outline"
            size="icon"
            disabled={cameras.length === 0}
            title="Open camera scanner"
          >
            <Camera className="h-4 w-4" />
          </Button>
          <Button
            onClick={handleManualSubmit}
            disabled={!manualInput.trim() || isProcessing}
            className={buttonClasses[config.color]}
          >
            {isProcessing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          </Button>
        </div>

        {/* Camera Dialog */}
        <Dialog open={showCameraDialog} onOpenChange={handleCloseDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <QrCode className="h-5 w-5" />
                {config.label}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div 
                id={scannerContainerId}
                className="w-full aspect-square bg-black rounded-lg overflow-hidden"
              />
              {lastResult && (
                <div className={`p-3 rounded-lg ${lastResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  {lastResult.success ? (
                    <div className="flex items-center gap-2">
                      <Check className="h-5 w-5" />
                      <span>{lastResult.message}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-5 w-5" />
                      <span>{lastResult.error}</span>
                    </div>
                  )}
                </div>
              )}
              <Button onClick={handleCloseDialog} variant="outline" className="w-full">
                <X className="h-4 w-4 mr-2" /> Close Scanner
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <Card className={`border-2 border-dashed ${colorClasses[config.color]}`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <QrCode className="h-5 w-5" />
          {config.label}
        </CardTitle>
        <CardDescription>{config.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Camera Error */}
        {cameraError && (
          <div className="p-3 bg-amber-50 text-amber-700 rounded-lg text-sm flex items-center gap-2">
            <CameraOff className="h-4 w-4 flex-shrink-0" />
            {cameraError}
          </div>
        )}

        {/* Manual Input */}
        <form onSubmit={handleManualSubmit} className="space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Keyboard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={manualInput}
                onChange={(e) => setManualInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter code manually or scan with hardware scanner"
                className="pl-10"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              onClick={startScanning}
              variant="outline"
              disabled={cameras.length === 0}
              className="gap-2"
            >
              <Camera className="h-4 w-4" />
              Camera Scan
            </Button>
            <Button
              type="submit"
              disabled={!manualInput.trim() || isProcessing}
              className={`gap-2 ${buttonClasses[config.color]}`}
            >
              {isProcessing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              Process
            </Button>
          </div>
        </form>

        {/* Last Result */}
        {lastResult && (
          <div className={`p-4 rounded-lg border-2 ${
            lastResult.success 
              ? 'bg-green-50 border-green-400' 
              : 'bg-red-50 border-red-400'
          }`}
          data-testid="scan-result"
          >
            <div className="flex items-center gap-3">
              {lastResult.success ? (
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                  <Check className="h-7 w-7 text-white" />
                </div>
              ) : (
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-500 flex items-center justify-center">
                  <AlertCircle className="h-7 w-7 text-white" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className={`font-semibold text-lg ${lastResult.success ? 'text-green-800' : 'text-red-800'}`}>
                  {lastResult.success ? lastResult.message : lastResult.error}
                </p>
                {lastResult.data?.delegate && (
                  <div className="mt-1 space-y-0.5">
                    <p className="text-sm font-medium text-slate-700" data-testid="scan-delegate-name">
                      {lastResult.data.delegate.name}
                      {lastResult.data.delegate.company && ` - ${lastResult.data.delegate.company}`}
                    </p>
                    {lastResult.data.delegate.tier && (
                      <p className="text-xs text-slate-500">Tier: {lastResult.data.delegate.tier}</p>
                    )}
                    {lastResult.data.delegate.province && (
                      <p className="text-xs text-slate-500">Province: {lastResult.data.delegate.province}</p>
                    )}
                    {(lastResult.data.delegate.meals || lastResult.data.delegate.meal_access) && (
                      <p className="text-xs text-slate-500">
                        Meals: {lastResult.data.delegate.meal_access ? 'Included' : (lastResult.data.delegate.meals || 'N/A')}
                      </p>
                    )}
                    {lastResult.data.delegate.accommodation && (
                      <p className="text-xs text-slate-500">Accommodation: {lastResult.data.delegate.accommodation}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Camera Dialog */}
        <Dialog open={showCameraDialog} onOpenChange={handleCloseDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Camera className="h-5 w-5" />
                {config.label} - Camera Scanner
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Scanner Container */}
              <div 
                id={scannerContainerId}
                className="w-full aspect-square bg-black rounded-lg overflow-hidden relative"
              >
                {!isScanning && (
                  <div className="absolute inset-0 flex items-center justify-center text-white">
                    <Loader2 className="h-8 w-8 animate-spin" />
                  </div>
                )}
              </div>
              
              {/* Scanning Indicator */}
              {isScanning && !isProcessing && (
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full">
                    <ScanLine className="h-4 w-4 animate-pulse" />
                    <span>Point camera at QR code</span>
                  </div>
                </div>
              )}
              
              {/* Processing Indicator */}
              {isProcessing && (
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-full">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Processing...</span>
                  </div>
                </div>
              )}

              {/* Result */}
              {lastResult && (
                <div className={`p-4 rounded-lg border-2 ${
                  lastResult.success 
                    ? 'bg-green-50 border-green-400 text-green-800' 
                    : 'bg-red-50 border-red-400 text-red-800'
                }`}
                data-testid="scan-dialog-result"
                >
                  <div className="flex items-center gap-3">
                    {lastResult.success ? (
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                        <Check className="h-6 w-6 text-white" />
                      </div>
                    ) : (
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-500 flex items-center justify-center">
                        <AlertCircle className="h-6 w-6 text-white" />
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-semibold">{lastResult.success ? lastResult.message : lastResult.error}</p>
                      {lastResult.data?.delegate && (
                        <div className="text-sm opacity-80 mt-0.5">
                          <p>{lastResult.data.delegate.name}</p>
                          {lastResult.data.delegate.tier && <p className="text-xs">Tier: {lastResult.data.delegate.tier}</p>}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Close Button */}
              <Button onClick={handleCloseDialog} variant="outline" className="w-full">
                <X className="h-4 w-4 mr-2" /> Close Scanner
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default UniversalScanner;
